/** @jsxImportSource @emotion/react */
import { Alert, Backdrop, Box, Button, CircularProgress, Container, Dialog, MenuItem, Select, SelectChangeEvent, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { IValidValue } from "../../Api/types";
import { getValidValues } from "../../Api/ValidValues";
import { toggleBtnsStyle } from '../../Styles/styling-variables';
import { setIsFulfilled, updateCmExtConnT2Thunk, setSelectedCmExternalConnT2} from '../../Slices/T2/cmExternalConnT2';
import ErrorBackdropComponent from "../ErrorBackdropComponent";
import BackdropComponent from "../BackdropComponent";
import DecimalInput from "../DecimalInput";

interface IProps {
    isDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CmExternalConnectionT2({isDialogOpen, setIsDialogOpen}: IProps) {
    const { cmExtConnInfo, isLoading, isRejected, isFulfilled, empId, contractType} = useSelector((state: RootState) => ({
        cmExtConnInfo: state.cmExternalConnT2.selectedExternalConn,
        isLoading: state.cmExternalConnT2.isLoading,
        isRejected: state.cmExternalConnT2.isRejected,
        isFulfilled: state.cmExternalConnT2.isFulfilled,
        empId: state.user.empId,
        contractType: state.user.contracttype
    }))
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dispatchThunk = useDispatch<AppDispatch>();
    const dispatch = useDispatch();
    const [bottomMarginContainer, setBottomMarginContainer] = useState<string>();
    const fixedContainerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [validValues, setValidValues] = useState<IValidValue[]>([]);
    //#region  Variables
    const tipoconCN = 'TIPOCON';
    const estadoconCN = 'ESTADOCON';
    const codtipoalimCN = 'CODTIPOALIM';
    //#endregion

    const [tipoconValue, setTipoconValue] = useState<string | null>(cmExtConnInfo?.tipocon ?? null);
    const handleTipoconSelection = (event: SelectChangeEvent) => {
        const value = event.target.value;
        if (value != null) { 
            setTipoconValue(value);            
        } else {
            setError("Seleccioneu un valor");
        }
    }
    const [estadoconValue, setEstadoconValue] = useState<number | null>(cmExtConnInfo?.estadocon ?? null);
    const [estadoconDescription, setEstadoconDescription] = useState<string>();
    const handleEstadoconSelection = (e: React.ChangeEvent<any>, value: string | null) => {
        if (value != null) { 
            setEstadoconValue(parseInt(value));
            const description = getDescriptionByKey(value.toString(), estadoconCN);
            setEstadoconDescription(description);
        } 
    }
    
    const [consumoValue, setConsumoValue] = useState<number | null>(cmExtConnInfo?.consumo ?? null);
    const handleConsumoChange = (value: number) => {
        setConsumoValue(value);
    }

    const [ciralimonValue, setCiralimonValue] = useState<string | null>(cmExtConnInfo?.ciralimon ?? null);

    
    useEffect(() => {
        const tableName = 'connection_t2';
        getValidValues(tableName)
            .then((values) => {
                setValidValues(values);
            })
            .catch((error) => {
                console.error(error);
            });            
    }, []);

    const getDescriptionByKey = (key: string, columnName: string): string => {
        const selectedValue = validValues.find(value => value.key === key && value.columnName.toUpperCase() === columnName.toUpperCase());
        return selectedValue ? selectedValue.value : '';
    }

    useEffect(() => {
        setTipoconValue(cmExtConnInfo?.tipocon ?? null);
        setEstadoconValue(cmExtConnInfo?.estadocon ?? null);
        setCiralimonValue(cmExtConnInfo?.ciralimon ?? null);
        if(validValues) {
            setEstadoconDescription(getDescriptionByKey(cmExtConnInfo?.estadocon?.toString() || '', estadoconCN));
        }

        if (!cmExtConnInfo?.id) {
            setIsDialogOpen(false);
        }
    }, [cmExtConnInfo, validValues])

    useEffect(() => {
        if (fixedContainerRef.current) {
            const fixedContainerHeight = fixedContainerRef.current.offsetHeight;
            setBottomMarginContainer(`${fixedContainerHeight + 60}px`);
        }
    }, [fixedContainerRef.current]);

    useEffect(() => {
        if (isLoading) {
            setIsOpen(true);
        }
        if (isFulfilled) {
            setIsOpen(true);
            setTimeout(() => {
                dispatch(setIsFulfilled(false));
                setIsOpen(false);
                setIsDialogOpen(false);
                setTipoconValue(null);
                setEstadoconValue(null);
                setCiralimonValue(null);
            }, 3000);
        }
        if (isRejected) {
            setIsOpen(true);
            setTimeout(() => {
                setIsOpen(false);
            }, 3000);
        }
        if (error) {
            setIsOpen(true);
            setTimeout(() => {
                setIsOpen(false);
                setError(null);
            }, 3000);
        }
    }, [dispatch, isFulfilled, isRejected, error])

    const handleSaveClick = async () => {
        if (cmExtConnInfo?.id && estadoconValue !== null && ciralimonValue !== null) {
            const updatedCmExtConn = {
                id: cmExtConnInfo.id,
                rid: cmExtConnInfo.rid,
                idCM: cmExtConnInfo.idCM ?? null,
                tipocon: tipoconValue ?? null,
                estadocon: estadoconValue ?? null,
                consumo: consumoValue ?? null,
                ciralimon: ciralimonValue,
                isInspected: "Y",
                empId: empId
            }
            if (updatedCmExtConn && contractType) {
                await dispatchThunk(updateCmExtConnT2Thunk({ updatedCmExtConn, contractType }));

            }
        } else if( estadoconValue === null) {
            setError("Indiqueu l'estat de la connexió");
        } else if (ciralimonValue === null) {
            setError("Indiqueu id circuit o codi -1");
        }
    }

    const handleCancelBtnClick = () => {
        setIsDialogOpen(false);
        dispatch(setSelectedCmExternalConnT2(null));
        setTipoconValue(null);
        setEstadoconValue(null);  
        setCiralimonValue(null);      
    }

    return (
        <Dialog open={isDialogOpen} fullScreen sx={{ height: '100vh' }}>
            <Container sx={{ marginTop: 'calc(1vw + 15px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 'calc(1vw + 6px)', marginBottom: bottomMarginContainer }}>
                <Box component="form" sx={{ '& .MuiTextField-root': { m: 1, width: '25ch' }, my: 2 }} noValidate autoComplete="off">
                    <Typography variant="h4" sx={{ marginY: 2 }} >Connexió aliena ({cmExtConnInfo?.id})</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle1' fontWeight={'medium'}>RID</Typography>
                        <TextField id="suport-id" variant="outlined" value={cmExtConnInfo?.rid} contentEditable={false} sx={{ backgroundColor: 'white', marginLeft: '0 !important' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle1' fontWeight={'medium'}>CENMANDO</Typography>
                        <TextField id="suport-id" variant="outlined" value={cmExtConnInfo?.idCM} contentEditable={false} sx={{ backgroundColor: 'white', marginLeft: '0 !important' }} />
                    </Box>
                    <Box sx={{ my: 1 }}>
                        <Typography variant='subtitle1' sx={{ my: 1 }} fontWeight={'medium'} >Tipus de connexió</Typography>
                        <Select
                            labelId="cm-type-select-label"
                            id="demo-simple-select"
                            value={tipoconValue || ''}
                            label="cm-type"
                            onChange={handleTipoconSelection}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                backgroundColor: 'white',

                            }}
                        >
                            {validValues
                                .filter(value => value.columnName === tipoconCN)
                                .map((value, index) => (
                                    <MenuItem key={index} value={value.key}>{`${value.key} - ${value.value}`}</MenuItem>
                                ))}
                        </Select>
                    </Box>
                    <Box>
                        <Typography variant='subtitle1' sx={{ my: 0.2 }} fontWeight={'medium'} >Estat connexió({estadoconDescription})</Typography>
                        {validValues && (
                            <ToggleButtonGroup
                                value={estadoconValue}
                                exclusive
                                onChange={handleEstadoconSelection}
                                aria-label="status"
                                size='large'
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                }}
                            > {validValues
                                .filter(value => value.columnName === estadoconCN)
                                .map((value, index) => (
                                    <ToggleButton key={index} value={parseInt(value.key)} aria-label={value.key} css={toggleBtnsStyle}>
                                        <Typography variant="subtitle1">{value.key}</Typography>
                                    </ToggleButton>
                                ))
                                }
                            </ToggleButtonGroup>
                        )}
                    </Box> 
                    <DecimalInput labelId='consumo' labelText='Consum' initialValue={consumoValue} onChange={handleConsumoChange}  /> 
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'start' }} >
                        <Typography variant='subtitle1' fontWeight={'medium'} >ID Circuit Monofàsic</Typography>
                        <TextField id='circuitName' type='text' value={ciralimonValue} onChange={(e) => setCiralimonValue(e.target.value)} sx={{ backgroundColor: 'white', marginLeft: '0 !important' }} />
                    </Box> 
                </Box>
                {error && (
                    <ErrorBackdropComponent error={error} isOpen={isOpen} />
                )}
                {(isLoading || isFulfilled || isRejected) && (
                    <BackdropComponent isLoading={isLoading} isFulfilled={isFulfilled} isRejected={isRejected} isOpen={isOpen} />
                )}
            </Container>
            <Box
                ref={fixedContainerRef}
                sx={{
                    width: '100%',
                    height: '7vh',
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    zIndex: 9999,
                    boxShadow: 'rgba(0, 0, 0, 0.35) 0px -2px 15px',
                    marginTop: 'calc(2vw + 10px)',
                }}>
                <Button onClick={handleCancelBtnClick} sx={{ fontSize: '1.2rem', color: '#9e9e9e', }} >Cancel·lar</Button>
                <Button onClick={handleSaveClick} sx={{ fontSize: '1.2rem', color: 'palette.primary.main', }} >Guardar</Button>
            </Box>
        </Dialog>
    )
}